import { apiError } from "../../../../../../utils/formErrors";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import React, { useState, useEffect } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Dialog, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import EmptyState from "../../../../../../component/EmptyState";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getProjects, createProject, deleteProject, getClients } from "../../../../../../services/timesheet";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";

const TONE = { active: "success", inactive: "muted" };

const ProjectConfiguration = () => {
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", client: "", billable: true });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true); setError(false);
    try {
      const [p, c] = await Promise.all([getProjects(), getClients().catch(() => null)]);
      if (p?.success === false) throw new Error(p?.message || "Failed to load projects");
      setRows(Array.isArray(p?.data) ? p.data : []);
      setClients(Array.isArray(c?.data) ? c.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load projects"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!form.name.trim()) { notify.error("Project name is required"); return; }
    setSaving(true);
    try {
      const res = await createProject({ ...form, client: form.client || undefined });
      if (res?.success === false) throw apiError(res, "Could not create project");
      notify.success("Project added");
      setOpen(false); setForm({ name: "", code: "", client: "", billable: true });
      fetchData();
    } catch (e) { notify.error(e?.message || "Could not create project"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await deleteProject(id); notify.success("Project removed"); fetchData(); }
    catch (e) { notify.error(e?.message || "Could not remove project"); }
  };

  const ch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Box sx={{ width: "100%", padding: "1.5rem 1.25rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Projects</Box>
        <BtnPrimary onClick={() => setOpen(true)} sx={{ borderRadius: "8px" }}>Add Project</BtnPrimary>
      </Stack>

      {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Name</TableCell>
              <TableCell sx={headCellSx}>Code</TableCell>
              <TableCell sx={headCellSx}>Client</TableCell>
              <TableCell sx={headCellSx}>Billable</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={{ ...headCellSx, width: "4rem" }}></TableCell>
            </TableRow>
          </TableHead>
            <TableBody><TableSkeleton columns={6} /></TableBody>
          </Table>
        ) : error ? (
        <EmptyState variant="error" title="Couldn't load projects" onRetry={fetchData} />
      ) : rows.length === 0 ? (
        <EmptyState title="No projects yet" description="Add projects so time can be logged against them." />
      ) : (
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Name</TableCell>
              <TableCell sx={headCellSx}>Code</TableCell>
              <TableCell sx={headCellSx}>Client</TableCell>
              <TableCell sx={headCellSx}>Billable</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={{ ...headCellSx, width: "4rem" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p._id}>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{p.name}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{p.code || "—"}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{p.client?.name || "—"}</TableCell>
                <TableCell sx={bodyCellSx}>{p.billable ? "Yes" : "No"}</TableCell>
                <TableCell sx={bodyCellSx}><StatusChip label={p.status || "active"} tone={TONE[p.status] || "success"} /></TableCell>
                <TableCell sx={bodyCellSx}>
                  <IconButton aria-label="Delete" size="small" onClick={() => remove(p._id)} sx={{ color: tokens.textMuted, "&:hover": { color: tokens.danger } }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "28rem", maxWidth: "94vw" } }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, mb: "1.25rem" }}>Add Project</Box>
        <FormField label="Project Name" type="text" value={form.name} onChange={(e) => ch("name", e.target.value)} fullwidth />
        <FormField label="Code" type="text" value={form.code} onChange={(e) => ch("code", e.target.value)} fullwidth />
        <FormField label="Client" type="select" options={[{ name: "— None —", value: "" }, ...clients.map((c) => ({ name: c.name, value: c._id }))]} value={form.client} onChange={(e) => ch("client", e.target.value)} fullwidth />
        <FormField label="Billable" type="select" options={[{ name: "Yes", value: true }, { name: "No", value: false }]} value={form.billable} onChange={(e) => ch("billable", e.target.value === "false" ? false : Boolean(e.target.value))} fullwidth />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "1.5rem" }}>
          <BtnSecondary onClick={() => setOpen(false)} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={submit} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? "Adding…" : "Add"}</BtnPrimary>
        </Stack>
      </Dialog>
    </Box>
  );
};

export default ProjectConfiguration;
